import { type, type Type } from 'arktype'
import { IntlMessageFormat } from 'intl-messageformat'

import { amountToString, isAmount, rateToPercentString, type AmountToStringOptions } from '@orus.eu/amount'
import { TechnicalError } from '@orus.eu/error'

import type { ArgumentValue, KeyParameters } from './generated/types.gen.ts'
import en from './translations/translations-en.json' with { type: 'json' }
import es from './translations/translations-es.json' with { type: 'json' }
import fr from './translations/translations-fr.json' with { type: 'json' }
import nl from './translations/translations-nl.json' with { type: 'json' }

export type { ArgumentValue, KeyParameters } from './generated/types.gen.ts'

export type TranslateOptions = Pick<AmountToStringOptions, 'currencyPosition'>

export type TranslationKey = keyof typeof fr

export const languages = ['fr', 'es', 'nl', 'en'] as const

export const languageSchema: Type<Language> = type('===', ...languages)

export type Language = (typeof languages)[number]

export const defaultLanguage: Language = 'fr'

export function isLanguage(value: string): value is Language {
  const languagesStrings: readonly string[] = languages
  return languagesStrings.includes(value)
}

export function assertIsLanguage(value: string): asserts value is Language {
  if (!isLanguage(value)) {
    throw new TechnicalError('Invalid language', { context: { value } })
  }
}

// Picks the array for the user's language when present and non-empty, otherwise the fallback language's array.
export function getLocalizedArrayForLanguage<T>(
  valuesByLanguage: Partial<Record<Language, T[]>>,
  userLanguage: Language | undefined | null,
  fallbackLanguage: Language,
): T[] | undefined {
  if (userLanguage && valuesByLanguage[userLanguage]?.length) return valuesByLanguage[userLanguage]
  return valuesByLanguage[fallbackLanguage]
}

export const languageLabels: Record<Language, string> = {
  fr: 'Français',
  es: 'Español',
  nl: 'Nederlands',
  en: 'English',
}

export const languageFlags: Record<Language, string> = {
  fr: '🇫🇷',
  es: '🇪🇸',
  nl: '🇳🇱',
  en: '🇬🇧',
}

export function getFormattedLanguageWithFlag(language: Language): string {
  return `${languageFlags[language]} ${languageLabels[language]}`
}

export type Translations = Record<TranslationKey, string>
export type PartialTranslations = Partial<Record<TranslationKey, string>>

const fallbackTranslations: Translations = fr

/**
 * Translations for all languages
 * Note that we don't expose this object directly to force the use of the translate / getIntlMessages
 * functions. This will allow use to control how the translations are used in the future
 * (e.g. value formatting etc...)
 */
const translations: Record<Language, PartialTranslations> = {
  en,
  es,
  fr,
  nl,
}

export type ParameterlessTranslationKey = Exclude<TranslationKey, keyof KeyParameters>

export type TranslationKeyParameters<Key extends TranslationKey> = Key extends keyof KeyParameters
  ? KeyParameters[Key]
  : undefined

export type LocalizableMessage<Key extends TranslationKey = TranslationKey> = Key extends ParameterlessTranslationKey
  ? ParameterlessTranslationKey
  : {
      key: Key
      parameters: TranslationKeyParameters<Key>
    }

declare const anyLocalizableMessageBrand: unique symbol

/**
 * Localization branded type built through `localizable()`
 */
export type AnyLocalizableMessage =
  | ParameterlessTranslationKey
  | {
      key: TranslationKey
      parameters: Record<string, ArgumentValue>
      readonly [anyLocalizableMessageBrand]: void
    }

export function localizable<Key extends TranslationKey>(message: LocalizableMessage<Key>): AnyLocalizableMessage {
  return message as AnyLocalizableMessage
}

/**
 * Translates a simple message without any parameters
 * @param key
 * @param language
 */
export function translate(key: ParameterlessTranslationKey, language: Language): string
/**
 * Translates a localizable message that wraps a translation key and its parameters
 * @param localizableMessage
 * @param language
 */
export function translate<Key extends TranslationKey>(
  localizableMessage: LocalizableMessage<Key>,
  language: Language,
): string
/**
 * Translates an erased localizable message (see AnyLocalizableMessage)
 * @param localizableMessage
 * @param language
 */
export function translate(localizableMessage: AnyLocalizableMessage, language: Language): string
/**
 * Translates an erased localizable message, formatting its amount parameters with the options
 * @param localizableMessage
 * @param language
 * @param parameters always undefined: the message carries its own
 * @param options
 */
export function translate(
  localizableMessage: AnyLocalizableMessage,
  language: Language,
  parameters: undefined,
  options: TranslateOptions,
): string
/**
 * Translates a message with parameters
 * @param key
 * @param language
 * @param parameters
 */
export function translate<Key extends keyof KeyParameters>(
  key: Key,
  language: Language,
  parameters: KeyParameters[Key],
  options?: TranslateOptions,
): string
export function translate(
  firstArg: TranslationKey | LocalizableMessage<TranslationKey> | AnyLocalizableMessage,
  language: Language,
  thridArg: Record<string, string> | undefined = undefined,
  options?: TranslateOptions,
): string {
  if (typeof firstArg === 'string' && !thridArg) {
    // first signature : parameterless translation key, we can just return the key
    const text =
      translations[language][firstArg as ParameterlessTranslationKey] ||
      fallbackTranslations[firstArg as ParameterlessTranslationKey]

    if (!text) {
      throw new TechnicalError('Missing translation', { context: { key: firstArg, language } })
    }

    return restoreLineBreaks(text)
  }

  const { key, parameters } =
    typeof firstArg === 'object'
      ? // second signature : localizable message
        firstArg
      : // thrird signature : key and parameters
        {
          key: firstArg as TranslationKey,
          parameters: thridArg,
        }

  const rawText = translations[language][key] || fallbackTranslations[key]

  if (!rawText) {
    throw new TechnicalError('Missing translation', { context: { key, language } })
  }

  const messageFormat = new IntlMessageFormat(restoreLineBreaks(rawText), language)
  return messageFormat.format(formatArgumentValues(parameters, options)) as string
}

// The translation files store a line break as the literal backslash-n (see ./lokalise/line-breaks.ts).
function restoreLineBreaks(value: string): string {
  return value.replaceAll('\\n', '\n')
}

// TODO: remove when everything is translated
export type PolicyTextValue = string | { key: string; parameters: Record<string, string> }
export function resolvePolicyText(text: PolicyTextValue, language: Language): string {
  if (typeof text === 'string') {
    if (Object.hasOwn(fallbackTranslations, text)) {
      return translate(text as ParameterlessTranslationKey, language)
    }
    return restoreLineBreaks(text)
  }
  if (typeof text === 'object' && 'key' in text && 'parameters' in text) {
    return translate(text.key as keyof KeyParameters, language, text.parameters as KeyParameters[keyof KeyParameters])
  }
  throw new TechnicalError('Invalid policy text value', { context: { text } })
}

/**
 * Create from the type of translate from @orus.eu/translations, but without the language parameter.
 * Used in contexts where we are going to translate a lot of strings using the
 * same language (like in the hook useTranslate, but not only)
 */
export type TranslateInSpecificLanguageFunction = {
  (key: ParameterlessTranslationKey): string
  <Key extends TranslationKey>(localizableMessage: LocalizableMessage<Key>): string
  (localizableMessage: AnyLocalizableMessage): string
  <Key extends keyof KeyParameters>(key: Key, parameters: KeyParameters[Key], options?: TranslateOptions): string
}

function createTranslateInSpecificLanguageFunction(language: Language): TranslateInSpecificLanguageFunction {
  // @ts-expect-error I don't know how to type this correctly, but cast is ok because we are in a well tested frameork function
  return (key, parameters, options) => translate(key, language, parameters, options)
}

export const translateIn: Record<Language, TranslateInSpecificLanguageFunction> = {
  en: createTranslateInSpecificLanguageFunction('en'),
  es: createTranslateInSpecificLanguageFunction('es'),
  fr: createTranslateInSpecificLanguageFunction('fr'),
  nl: createTranslateInSpecificLanguageFunction('nl'),
}

// Falls back to French per key, so callers never receive an empty message.
export function getIntlMessages(language: Language): Record<string, string> {
  const messages: Record<string, string> = { ...fallbackTranslations }
  for (const [key, text] of Object.entries(translations[language])) {
    if (text) messages[key] = text
  }
  return messages
}

function isTranslationKey(key: string): key is TranslationKey {
  return Object.hasOwn(fr, key)
}

// Callers pass keys sourced from arbitrary config (e.g. rule definitions), not necessarily
// valid translation keys, so this narrows before indexing rather than trusting the caller's type.
export function hasTranslationIn(language: Language, key: string): boolean {
  return isTranslationKey(key) && !!translations[language][key]
}

function formatArgumentValues(
  values: Record<string, ArgumentValue> | undefined,
  options?: TranslateOptions,
): Record<string, string> | undefined {
  if (!values) return values
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, formatArgumentValue(value, options)]))
}

function formatArgumentValue(value: ArgumentValue, options?: TranslateOptions): string {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
      return value.toFixed(0)
    default:
      return isAmount(value)
        ? amountToString(value, {
            addCurrency: true,
            displayDecimals: 'when-amount-has-cents',
            ...(options?.currencyPosition ? { currencyPosition: options.currencyPosition } : {}),
          })
        : rateToPercentString(value)
  }
}
